'use client'

import React, { useEffect, useState, useCallback } from "react";
import { useSelector } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { RootState } from '@/redux/store';
import { useGetSynopsisDataQuery, useUpdateSynopsisDataMutation } from '@/redux/slices/summary/summary-api-slice';
import { useGetPalletsForTestStackQuery } from "@/redux/slices/preship/preship-api-slice";
import {
  useGetLeanConsolidatedDataQuery,
  useGetUnitLoadPositionConsolidatedDataQuery,
  useGetUnitLoadHeightConsolidatedDataQuery
} from "@/redux/slices/preship/preship-api-slice";
import { useGetFTLConsolidatedDataQuery } from "@/redux/slices/pallet/palletApiSlice";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import Footer from "./footer";
import { cn } from "@/lib/utils";
import { showSuccessToast, showErrorToast } from "./toast/toast";
import AlertTooltip from "./alert-tooltip";
import { SynopsisService } from "@/services/SynopsisService";
import { StackTestLayoutService } from "@/services/StackTestLayoutService";
import { refetchAfterSave } from "@/lib/refetchAfterSave";
const failureModeLabels = {
  preShipLean: "Pre-Ship Lean",
  postShipLean: "Post-Ship Lean",
  preShipFTL: "Pre-Ship FTL",
  postShipFTL: "Post-Ship FTL",
  shipTestWalk: "Post-Ship Position (Walk)",
  stackTestLeanWeekly: "Stack Test Lean Weekly",
  stackTestCompressionLoadOn: "Stack Height Compression",
  stackTestCompressionLoadRemoval:"Stack Height Rebound"
};

const holdDuration = [
  'Week 1',
  "Week 2",
  "Week 3",
  "Week 4"
];

const stackPatterns = [
  { name: "2-HIGH COLUMN", minPallets: 6 },
  { name: "2-HIGH PYRAMID", minPallets: 6 },
  { name: "3-HIGH COLUMN", minPallets: 12 },
  { name: "3-HIGH PYRAMID", minPallets: 10 },
  { name: "4-HIGH COLUMN", minPallets: 16 },
  { name: "4-HIGH PYRAMID", minPallets: 14 },
];

interface SynopsisData {
  projectName: string;
  projectDescription: string;
  overallDecision: string;
  overallDecisionFailureNote: string;
  validStackingTest: string;
  reasonForInvalidStackingTest: string;
  failureModes: Record<string, Record<string, string>>;
  newFailureModes: Array<{ failureModeName: string; status: string }>;
}
interface SynopsisProps {
  isOffline: boolean;
}

const decisionValues = ['pass', 'fail', 'conditionalPass', 'pending'];

const Synopsis: React.FC<SynopsisProps> = ({ isOffline=false }: SynopsisProps) => {
  const { projectId } = useSelector((state: RootState) => state.define);
  const { data: synopsisData, isLoading, isFetching, isError, refetch } = useGetSynopsisDataQuery(projectId, { refetchOnMountOrArgChange: true });
  const [offlineSynopsisData, setOfflineSynopsisData] = useState<any | null>(null);
  const [updateSynopsisData] = useUpdateSynopsisDataMutation();
  const { data: pallets, isLoading: isStackTestLoading, error: stackTestError } = useGetPalletsForTestStackQuery(projectId, { refetchOnMountOrArgChange: true });
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [offlinePallets, setOfflinePallets] = useState<any[]>([]);
  const [consolidatedDataReady, setConsolidatedDataReady] = useState(false);
  const [mergedFailureModes, setMergedFailureModes] = useState<Record<string, Record<string, string>>>({});
  console.log('Synopsis data received from API:', synopsisData)
  // Fetch consolidated data for more accurate failure mode statuses
  // const { data: preShipLeanData,isFetching: isPreShipLeanLoading } = useGetLeanConsolidatedDataQuery({
  //   projectId, variant: 'preShip'
  // }, { refetchOnMountOrArgChange: true });

  // const { data: postShipLeanData,isFetching: isPostShipLeanLoading } = useGetLeanConsolidatedDataQuery({
  //   projectId, variant: 'postShip'
  // }, { refetchOnMountOrArgChange: true });

  // const { data: postStackLeanData,isFetching: isPostStackLeanLoading } = useGetLeanConsolidatedDataQuery({
  //   projectId, variant: 'postStack', isSum: true
  // }, { refetchOnMountOrArgChange: true });

  // const { data: preShipFTLData,isFetching: isPreShipFTLLoading } = useGetFTLConsolidatedDataQuery({
  //   projectId, variant: 'preShip'
  // }, { refetchOnMountOrArgChange: true });

  // const { data: postShipFTLData,isFetching: isPostShipFTLLoading } = useGetFTLConsolidatedDataQuery({
  //   projectId, variant: 'postShip'
  // }, { refetchOnMountOrArgChange: true });

  // const { data: shipTestWalkData,isFetching: isShipTestWalkLoading } = useGetUnitLoadPositionConsolidatedDataQuery({
  //   projectId, variant: 'postShip'
  // }, { refetchOnMountOrArgChange: true });
  // const { data: stackTestLeanWeekly,isFetching: isStackTestLeanWeeklyLoading } = useGetLeanConsolidatedDataQuery({
  //   projectId, variant: 'postStack', isSum: true
  // }, { refetchOnMountOrArgChange: true });
  // const { data: stackTestCompressionData,isFetching: isStackTestCompressionLoading } = useGetUnitLoadHeightConsolidatedDataQuery({
  //   projectId, variant: 'postStack', isSum: true
  // }, { refetchOnMountOrArgChange: true });

  const initialValues: SynopsisData = {
    projectName: "",
    projectDescription: "",
    overallDecision: "pending",
    overallDecisionFailureNote: "",
    validStackingTest: "no",
    reasonForInvalidStackingTest: "",
    failureModes: {},
    newFailureModes: [{ failureModeName: "N/A", status: "fail" }],
  };
  // console.log(postShipLeanData,'postShipLeanData')
  // Function to determine pass/fail status from consolidated data
  const determineFailureModeStatus = (additionalStats: any) => {
    if (!additionalStats) return 'dataMissing';

    // Check data fill status first
    if (additionalStats.dataFillStatus === 'dataMissing' || additionalStats.dataFillStatus === 'N/A' || additionalStats.dataFillStatus === 'Incomplete') {
      return 'dataMissing';
    }

    // If there's any data but the overall status is 'Fail', return 'fail'
    if (additionalStats.overallStatus === 'Fail') {
      return 'fail';
    }

    // Otherwise, if the data is complete or incomplete but passes, return 'pass'
    if (additionalStats.overallStatus === 'Pass') {
      return 'pass';
    }

    // If we can't determine, default to data missing
    return 'dataMissing';
  };


  // Modify this useEffect to stop updating formik values in the merging logic
  // useEffect(() => {
  //   // Process consolidated data when it's available
  //   if (synopsisData?.failureModes && !isOffline) {
  //     // Only set the merged failure modes once when the data is first loaded
  //     if (!consolidatedDataReady) {
  //       // Create a deep copy of the failureModes object to avoid modifying read-only properties
  //       const accurateFailureModes = JSON.parse(JSON.stringify(synopsisData.failureModes));

  //       // Process each variant (pallet type)
  //       Object.keys(accurateFailureModes).forEach(variant => {
  //         console.log(variant, 'variant')
  //         // Pre-Ship Lean
  //         if (preShipLeanData?.additionalStats) {
  //           console.log(`Pre-Ship Lean data for ${variant}:`, preShipLeanData)
  //           const variantStats = preShipLeanData.additionalStats.find(
  //             (stats: any) => {
  //               // Fix the comparison - extract the variant part from the stats.rowLabel
  //               const parts = variant.split(" - ");
  //               return parts.length > 1 && parts[1] === stats.rowLabel;
  //             }
  //           );
  //           console.log(`Pre-Ship Lean variant stats for ${variant}:`, variantStats)
  //           if (variantStats) {
  //             accurateFailureModes[variant].preShipLean = determineFailureModeStatus(variantStats);
  //           }
  //         }

  //         // Post-Ship Lean
  //         if (postShipLeanData?.additionalStats) {
  //           const variantStats = postShipLeanData.additionalStats.find(
  //             (stats: any) => {
  //               const parts = variant.split(" - ");
  //               return parts.length > 1 && parts[1] === stats.rowLabel;
  //             }
  //           );
  //           console.log(variantStats,'variantStats')
  //           if (variantStats) {
  //             accurateFailureModes[variant].postShipLean = determineFailureModeStatus(variantStats);
  //             console.log(accurateFailureModes[variant].postShipLean,'accurateFailureModes[variant].postShipLean')
  //           }
  //         }

  //         // Stack Test Lean Weekly
  //         if (postStackLeanData?.additionalStats) {
  //           const variantStats = postStackLeanData.additionalStats.find(
  //             (stats: any) => {
  //               const parts = variant.split(" - ");
  //               return parts.length > 1 && parts[1] === stats.rowLabel;
  //             }
  //           );
  //           if (variantStats) {
  //             accurateFailureModes[variant].stackTestLeanWeekly = determineFailureModeStatus(variantStats);
  //           }
  //         }

  //         // Pre-Ship FTL
  //         if (preShipFTLData?.additionalStats) {
  //           const variantStats = preShipFTLData.additionalStats.find(
  //             (stats: any) => {
  //               const parts = variant.split(" - ");
  //               return parts.length > 1 && parts[1] === stats.rowLabel;
  //             }
  //           );
  //           if (variantStats) {
  //             accurateFailureModes[variant].preShipFTL = determineFailureModeStatus(variantStats);
  //           }
  //         }

  //         // Post-Ship FTL
  //         if (postShipFTLData?.additionalStats) {
  //           const variantStats = postShipFTLData.additionalStats.find(
  //             (stats: any) => {
  //               const parts = variant.split(" - ");
  //               return parts.length > 1 && parts[1] === stats.rowLabel;
  //             }
  //           );
  //           if (variantStats) {
  //             accurateFailureModes[variant].postShipFTL = determineFailureModeStatus(variantStats);
  //           }
  //         }

  //         // Ship Test Walk (Unit Load Position)
  //         if (shipTestWalkData?.additionalStats) {
  //           const variantStats = shipTestWalkData.additionalStats.find(
  //             (stats: any) => {
  //               const parts = variant.split(" - ");
  //               return parts.length > 1 && parts[1] === stats.rowLabel;
  //             }
  //           );
  //           console.log(`Ship Walk Test for ${variant}:`, variantStats)
  //           if (variantStats) {
  //             accurateFailureModes[variant].shipTestWalk = determineFailureModeStatus(variantStats);
  //           }
  //         }

  //         // Stack Test Lean Weekly - Fix the bug where it was using shipTestWalkData instead of stackTestLeanWeekly
  //         if (stackTestLeanWeekly?.additionalStats) {
  //           const variantStats = stackTestLeanWeekly.additionalStats.find(
  //             (stats: any) => {
  //               const parts = variant.split(" - ");
  //               return parts.length > 1 && parts[1] === stats.rowLabel;
  //             }
  //           );
  //           console.log(`Stack Test Lean Weekly for ${variant}:`, variantStats)
  //           if (variantStats) {
  //             accurateFailureModes[variant].stackTestLeanWeekly = determineFailureModeStatus(variantStats);
  //           }
  //         }

  //         // Stack Test Compression (Unit Load Height)
  //         if (stackTestCompressionData?.additionalStats) {
  //           const variantStats = stackTestCompressionData.additionalStats.find(
  //             (stats: any) => {
  //               const parts = variant.split(" - ");
  //               return parts.length > 1 && parts[1] === stats.rowLabel;
  //             }
  //           );
  //           console.log(`Stack Test Compression for ${variant}:`, variantStats)
  //           if (variantStats) {
  //             accurateFailureModes[variant].stackTestCompression = determineFailureModeStatus(variantStats);
  //           }
  //         }
  //       });

  //       // Set the merged modes just once
  //       setMergedFailureModes(accurateFailureModes);
  //       setConsolidatedDataReady(true);
  //       console.log(accurateFailureModes, 'accurateFailureModes')
  //     }
  //   }

  // }, [
  //   synopsisData?.failureModes,
  //   isOffline,
  //   preShipLeanData,
  //   postShipLeanData,
  //   postStackLeanData,
  //   preShipFTLData,
  //   postShipFTLData,
  //   shipTestWalkData,
  //   stackTestCompressionData,
  //   determineFailureModeStatus,
  //   consolidatedDataReady
  // ]);


  const formik = useFormik<SynopsisData>({
    initialValues: synopsisData || initialValues,
    validationSchema: Yup.object({
      overallDecision: Yup.string()
        .oneOf(decisionValues, 'Overall Decision is required')
        .required('Overall Decision is required'),
      overallDecisionFailureNote: Yup.string().when('overallDecision', {
        is: (overallDecision: string) => !['pass', 'pending'].includes(overallDecision),
        then: (schema) => schema.trim().required('Failure Notes are required when a failure is selected'),
        otherwise: (schema) => schema.notRequired(),
      }),
    }),
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        if (!isOffline) {
          // Use the merged failure modes instead of the values.failureModes
          const dataToSubmit = {
            ...values,
            failureModes: mergedFailureModes, // Use the separately managed failure modes
          };
          await updateSynopsisData({ projectId, ...dataToSubmit }).unwrap();
          console.log({ projectId, ...dataToSubmit },'data beinng submitted')
          refetchAfterSave(refetch);
          showSuccessToast('Synopsis data saved successfully');
          setHasUnsavedChanges(false);
        } else {
          await SynopsisService.updateSynopsisData(Number(projectId), values as any);
          showSuccessToast('Synopsis data saved successfully');
          setHasUnsavedChanges(false);
        }
      } catch (error) {
        console.error('Error saving synopsis data:', error);
        showErrorToast('Failed to save synopsis data');
      }
    },
  });

  useEffect(() => {
    if (isOffline) {
      const getData = async () => {
        try {
          const data = await SynopsisService.getSynopsisData(Number(projectId));
          setOfflineSynopsisData(data);
        } catch (error) {
          console.error('Error getting synopsis data:', error);
        }
      }
      getData();
    }
  }, [isOffline, synopsisData, projectId]);

  useEffect(() => {
    if (synopsisData && !isOffline) {
      // Only set the basic form data, excluding failure modes which we'll manage separately
      formik.setValues({
        ...synopsisData,
        // Keep the existing failureModes object but don't try to update it with accurate values
        // This prevents triggering renders when failure mode data changes
        newFailureModes: synopsisData.newFailureModes?.length > 0
          ? synopsisData.newFailureModes
          : [{ failureModeName: "N/A", status: "fail" }],
      }, false); // false prevents validation from running which can trigger rerenders
    } else if (isOffline && offlineSynopsisData) {
      console.log(offlineSynopsisData,'offlineSynopsisData')
      formik.setValues({ ...offlineSynopsisData }, false);

      if (offlineSynopsisData?.newFailureModes) {
        formik.setFieldValue(
          "newFailureModes",
          offlineSynopsisData.newFailureModes.map((nfm: any) => ({
            ...nfm,
            failureModeName: (nfm.failureModeName?.trim()?.length ? nfm.failureModeName : "N/A"),
          }))
        );
      }

    }
  }, [
    synopsisData,
    isOffline,
    offlineSynopsisData
    // Explicitly NOT including formik in dependencies to prevent circular updates
  ]);
console.log(synopsisData,'synopsisData')
  // Add back the missing useEffect for tracking unsaved changes
  useEffect(() => {
    const hasChanges = JSON.stringify(formik.initialValues) !== JSON.stringify(formik.values);
    setHasUnsavedChanges(hasChanges);
  }, [formik.values, formik.initialValues]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pass':
        return 'bg-states-success text-black  text-white font-semibold';
      case 'conditionalPass':
        return 'bg-states-success text-black  text-white font-semibold';
      case 'fail':
        return 'bg-bgRed text-black  text-white font-semibold';
      case 'dataMissing':
        return 'bg-[#F79E19] text-black  text-white font-semibold';
      default:
        return 'bg-white';
    }
  };
  console.log()
  const stackFormik = useFormik({
    initialValues: {
      pallets: [],
    },
    onSubmit: async (values) => {
      // Implementation for stack test submission
      console.log('Stack test values:', values);
    },
  });
  useEffect(() => {
    if (isOffline) {
      const getData = async () => {
        try {
          const data = await StackTestLayoutService.getPalletsForTestStack(Number(projectId));
          setOfflinePallets(data);
        } catch (error) {
          console.error('Error getting pallets for test stack:', error);
        }
      }
      getData();
    }
  }, [isOffline, projectId])
  useEffect(() => {
    if (pallets && !isOffline) {
      stackFormik.setValues({
        pallets: pallets.map((pallet: any) => ({
          id: pallet.id,
          name: pallet.name,
          stackTestLayoutWeek: pallet.stackTestLayoutWeek || "",
          stackPattern: pallet.stackPattern || "",
          lpnCount: pallet.lpnCount,
          recommendedMinimumPallets: stackPatterns.find(p => p.name === pallet.stackPattern)?.minPallets || "",
        })),
      });
    } else if (isOffline && offlinePallets) {
      stackFormik.setValues({
        pallets: offlinePallets.map((pallet: any) => ({
          id: pallet.id,
          name: pallet.name,
          stackTestLayoutWeek: pallet.stackTestLayoutWeek || "",
          stackPattern: pallet.stackPattern || "",
          lpnCount: pallet.lpnCount,
          recommendedMinimumPallets: stackPatterns.find(p => p.name === pallet.stackPattern)?.minPallets || "",
        })),
      });
    }
  }, [pallets, isOffline, offlinePallets]);

  const handleSave = useCallback(async () => {
    const sanitizedNewFailureModes = formik.values.newFailureModes.map((fm) => ({
      ...fm,
      failureModeName:
        (fm.failureModeName ?? '').trim() === '' ? 'N/A' : fm.failureModeName.trim(),
    }));
  
    formik.setFieldValue('newFailureModes', sanitizedNewFailureModes, false);

    await formik.submitForm();
  }, [formik]);

  const hasValidDecision = decisionValues.includes(formik.values.overallDecision);
  const hasFailureNotes = formik.values.overallDecisionFailureNote.trim().length > 0;
  const requiresFailureNotes = !['pass', 'pending'].includes(formik.values.overallDecision);
  const isSynopsisComplete = hasValidDecision && (!requiresFailureNotes || hasFailureNotes);
  console.log(formik.values, 'formik.values')
  if ((isFetching || isStackTestLoading) && !synopsisData && !isOffline) {
    return (
      <div className="w-full h-screen flex items-center justify-center">
        <div className="p-8 bg-white rounded-md shadow-md text-center">
          <div className="w-8 h-8 rounded-full border-4 border-blue-600 border-t-transparent animate-spin mx-auto mb-4"></div>
          <h3 className="text-lg font-semibold text-gray-700">Loading synopsis data...</h3>
          <p className="text-gray-500 mt-2">Please wait while we load the initial data.</p>
        </div>
      </div>
    );
  }
  // if (isError || stackTestError) return <div>Error loading data</div>;
  console.log(formik.values.failureModes, 'formik.values.failureModes','isOffline',isOffline,mergedFailureModes,'mergedFailureModes')
  return (
    <form onSubmit={formik.handleSubmit} className="w-full p-8 space-y-6 h-[calc(100vh-140px)] overflow-y-scroll">
      <h3 className="text-black font-semibold text-lg">Project Details</h3>
      <div className="space-y-4">
        <div className="w-full border rounded-r-md flex">
          <div className="max-w-[180px] label-bg">
            <Label className="label">Project Name</Label>
          </div>
          <div className="relative w-full">
            <Input
              readOnly
              placeholder="Enter the Project Name"
              {...formik.getFieldProps('projectName')}
              className={cn(
                "border-2 border-gray-200 bg-states-disabled",
                formik.touched.projectName && formik.errors.projectName &&
                "border-2 border-states-error outline-none"
              )}
            />
            {formik.touched.projectName && formik.errors.projectName && (
              <AlertTooltip message={formik.errors.projectName} />
            )}
          </div>
        </div>

        <div className="w-full border rounded-r-md flex">
          <div className="max-w-[180px] label-bg">
            <Label className="label">Project Description</Label>
          </div>
          <div className="relative w-full">
            <Textarea
              readOnly
              placeholder="Enter the Project Description"
              {...formik.getFieldProps('projectDescription')}
              className={cn(
                "border-2 border-gray-200 bg-states-disabled",
                formik.touched.projectDescription && formik.errors.projectDescription &&
                "border-2 border-states-error outline-none"
              )}
            />
            {formik.touched.projectDescription && formik.errors.projectDescription && (
              <AlertTooltip message={formik.errors.projectDescription} />
            )}
          </div>
        </div>

        <div className="w-full border rounded-r-md flex">
          <div className="max-w-[180px] label-bg">
            <Label className="label">Overall Decision *</Label>
          </div>
          <div className="relative w-full">
            <Select
              value={formik.values.overallDecision}
              onValueChange={(value) => {
                if (value !== '') {
                  formik.setFieldValue("overallDecision", value)
                }
              }}
            >
              <SelectTrigger className={cn("w-full", getStatusColor(formik.values.overallDecision))}>
                <SelectValue placeholder="Select decision" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pass">Pass</SelectItem>
                <SelectItem value="fail">Fail</SelectItem>
                <SelectItem value="conditionalPass">Conditional pass</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
              </SelectContent>
            </Select>
            {formik.touched.overallDecision && formik.errors.overallDecision && (
              <AlertTooltip message={formik.errors.overallDecision} />
            )}
          </div>
        </div>

        <div className="w-full border rounded-r-md flex">
          <div className="max-w-[180px] label-bg">
            <Label className="label">Failure Notes {requiresFailureNotes ? '*' : ''}</Label>
          </div>
          <div className="relative w-full">
            <Textarea
              disabled={formik.values.overallDecision === 'pass'}
              placeholder="Enter Failure Notes"
              {...formik.getFieldProps('overallDecisionFailureNote')}
              className={cn(
                "border-2 border-gray-200 disabled:bg-states-disabled",
                formik.touched.overallDecisionFailureNote && formik.errors.overallDecisionFailureNote &&
                "border-2 border-states-error outline-none"
              )}
            />
            {formik.touched.overallDecisionFailureNote && formik.errors.overallDecisionFailureNote && (
              <AlertTooltip message={formik.errors.overallDecisionFailureNote} />
            )}
          </div>
        </div>
      </div>
{ !isOffline &&
      <div className="mt-5">
        <h3 className="text-black font-semibold text-lg mb-2">Failure Modes</h3>

      
          <Table className="border-collapse">
            <TableHeader>
              <TableRow>
                <TableHead className="border border-gray-300 max-w-[50px] text-black p-0 px-3 text-[10px] md:text-sm font-semibold md:font-bold bg-light-blue text-left ">
                  Failure Modes
                </TableHead>
                {Object.keys( formik.values.failureModes ).map((variant) => (
                  <TableHead
                    key={variant}
                    className="border border-gray-300 text-black p-0 px-3 text-[10px] md:text-sm font-semibold md:font-bold bg-light-blue text-left max-w-[200px]"
                  >
                    {variant}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {Object.entries(failureModeLabels).map(([key, label]) => (
                <TableRow key={key}>
                  <TableCell className="border border-gray-300 bg-states-disabled p-0 px-3 font-semibold">
                    {label}
                  </TableCell>
                  {Object.keys(formik.values.failureModes).map((variant) => {
                    // Use the correct source of data based on online/offline state
                    const status = formik.values.failureModes[variant]?.[key] || 'dataMissing'
                      // : mergedFailureModes[variant]?.[key] || 'dataMissing';

                    return (
                      <TableCell key={`${key}-${variant}`} className="border border-gray-300 p-0">
                        <Select
                          value={status}
                          onValueChange={(value) => {
                            if (isOffline) {
                              // For offline mode, update formik as before
                              formik.setFieldValue(`failureModes.${variant}.${key}`, value);
                            } else {
                              // For online mode, only update our separate state
                              // Create a deep copy of the mergedFailureModes to avoid modifying read-only properties
                              const updatedModes = JSON.parse(JSON.stringify(mergedFailureModes));
                              if (!updatedModes[variant]) updatedModes[variant] = {};
                              updatedModes[variant][key] = value;
                              setMergedFailureModes(updatedModes);

                              // DO NOT update formik values here to avoid triggering the loop
                            }
                          }}
                        >
                          <SelectTrigger
                            className={`rounded-none border-0 ${getStatusColor(status)}`}
                          >
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pass">Pass</SelectItem>
                            <SelectItem value="fail">Fail</SelectItem>
                            <SelectItem value="dataMissing">Incomplete</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        
      </div>}

      <Table className="border-collapse mt-5">
        <TableHeader>
          <TableRow>
            <TableHead className="border border-gray-300 text-black p-0 px-3 text-[10px] md:text-sm font-semibold md:font-bold bg-light-blue text-left max-w-[200px]">
              New Failure Modes That are Not Listed
            </TableHead>
            <TableHead className="border border-gray-300 text-black p-0 px-3 text-[10px] md:text-sm font-semibold md:font-bold bg-light-blue text-left max-w-[200px]">
              Status
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {formik.values.newFailureModes.map((failureMode, index) => (

            <TableRow key={index}>
              <TableCell className="border border-gray-300 bg-white p-0 px-1 font-semibold">
                <Input
                  className="w-full h-9 border-none"
                  placeholder="Enter"
                  {...formik.getFieldProps(`newFailureModes.${index}.failureModeName`)}
                  
                  onBlur={() => {
                      const current = formik.values.newFailureModes[index]?.failureModeName;
                      if (!current?.trim().length) {
                        formik.setFieldValue(`newFailureModes.${index}.failureModeName`, "N/A");
                      }
                    }}
                />
              </TableCell>
              <TableCell className="border border-gray-300 p-0">
                <Select
                  value={failureMode.status}
                  onValueChange={(value) => {
                    if(value!==''){
                      formik.setFieldValue(`newFailureModes.${index}.status`, value)
                    }
                  }}
                >
                  <SelectTrigger className={`rounded-none border-0 ${getStatusColor(failureMode.status)}`}>
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pass">Pass</SelectItem>
                    <SelectItem value="fail">Fail</SelectItem>
                    <SelectItem value="dataMissing">Incomplete</SelectItem>
                  </SelectContent>
                </Select>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Button
        type="button"
        onClick={() => formik.setFieldValue('newFailureModes', [
          ...formik.values.newFailureModes,
          { failureModeName: "", status: "fail" }
        ])}
        className="mt-2 bg-blue"
      >
        Add New Failure Mode
      </Button>

      {/* <div className="w-full border rounded-r-md flex">
        <div className="max-w-[180px] label-bg">
          <Label className="label">Is Stacking test Valid ?</Label>
        </div>
        <div className="relative w-full">
          <Select
            value={formik.values.validStackingTest}
            onValueChange={(value) => formik.setFieldValue("validStackingTest", value)}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="yes">Yes</SelectItem>
              <SelectItem value="no">No</SelectItem>
            </SelectContent>
          </Select>
          {formik.touched.validStackingTest && formik.errors.validStackingTest && (
            <AlertTooltip message={formik.errors.validStackingTest} />
          )}
        </div>
      </div>

      <div className="w-full border rounded-r-md flex">
        <div className="max-w-[180px] label-bg">
          <Label className="label">Reason for not valid test</Label>
        </div>
        <div className="relative w-full">
          <Textarea
            placeholder="Enter the reason for not valid test"
            {...formik.getFieldProps('reasonForInvalidStackingTest')}
            className={cn(
              "border-2 border-gray-200",
              formik.touched.reasonForInvalidStackingTest && formik.errors.reasonForInvalidStackingTest &&
              "border-2 border-states-error outline-none"
            )}
          />
          {formik.touched.reasonForInvalidStackingTest && formik.errors.reasonForInvalidStackingTest && (
            <AlertTooltip message={formik.errors.reasonForInvalidStackingTest} />
          )}
        </div>
      </div> */}

      <div className="border-0 rounded-md overflow-x-auto pb-[100px]">
        <Table className="border-collapse w-full">
          <TableHeader>
            <TableRow>
              <TableHead
                className="border border-gray-300 text-black p-0 px-3 text-[10px] md:text-sm font-semibold md:font-bold bg-light-blue text-center"
                colSpan={5}
              >
                Stacktest Validation Details
              </TableHead>
            </TableRow>
            <TableRow>
              {[
                "Pallet Name",
                "Stack Pattern",
                "Stack Hold Duration",
                "Recommended minimum number of pallets for stack testing",
                // "Number of Pallets used for stack testing",
              ].map((head, headIndex) => (
                <TableHead
                  key={`${headIndex}-${head}`}
                  className="border border-gray-300 text-black p-0 px-3 text-[10px] md:text-sm font-semibold md:font-bold bg-light-blue text-left max-w-[200px]"
                >
                  {head}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {stackFormik.values.pallets?.map((pallet: any, index: number) => (
              <TableRow key={pallet.id}>
                <TableCell className={cn("border border-gray-300 bg-states-disabled p-0")}>
                  <Input
                    className="w-full h-9 border-none"
                    value={pallet.name}
                    readOnly
                  />
                </TableCell>
                <TableCell className={cn("border border-gray-300 bg-states-disabled p-0")}>
                  <Select
                    disabled={true}
                    onValueChange={(value) => {
                      stackFormik.setFieldValue(`pallets.${index}.stackPattern`, value);
                      const minPallets = stackPatterns.find(p => p.name === value)?.minPallets || "";
                      stackFormik.setFieldValue(`pallets.${index}.recommendedMinimumPallets`, minPallets);
                    }}
                    value={pallet.stackPattern}
                  >
                    <SelectTrigger className="w-full h-9 border-none">
                      <SelectValue placeholder="Select pattern" />
                    </SelectTrigger>
                    <SelectContent>
                      {stackPatterns.map((pattern) => (
                        <SelectItem key={pattern.name} value={pattern.name}>
                          {pattern.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell className={cn("border border-gray-300 p-0")}>
                  <Select
                    disabled={true}
                    onValueChange={(value) => stackFormik.setFieldValue(`pallets.${index}.stackTestLayoutWeek`, value)}
                    value={pallet.stackTestLayoutWeek}
                  >
                    <SelectTrigger className="w-full h-9 border-none">
                      <SelectValue placeholder="Select duration" />
                    </SelectTrigger>
                    <SelectContent>
                      {holdDuration.map((week) => (
                        <SelectItem key={week} value={week}>
                          {week}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell className={cn("border border-gray-300 bg-states-disabled p-0")}>
                  <Input
                    className="w-full h-9 border-none"
                    value={pallet.recommendedMinimumPallets}
                    readOnly
                  />
                </TableCell>
                {/* <TableCell
                  className={cn(
                    "border border-gray-300 p-0",
                    pallet.stackPattern 
                    && (
                      pallet.lpnCount < pallet.recommendedMinimumPallets
                        ? "bg-bgRed text-white"
                        : "bg-states-success text-white"
                    )
                  )}
                >
                  <Input
                    className="w-full h-9 border-none bg-transparent"
                    value={pallet.lpnCount < pallet.recommendedMinimumPallets ? "Invalid" : "Valid"}
                    readOnly
                  />
                </TableCell> */}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Footer
        isLoading={formik.isSubmitting}
        onSubmit={handleSave}
        hasUnsavedChanges={hasUnsavedChanges}
        reportDisabled={!isSynopsisComplete}
        showPushTo3DX
        pushTo3DXDisabled={!isSynopsisComplete || !hasValidDecision}
        dtrId={synopsisData?.dtrId}
      />
    </form>
  );
};

export default Synopsis;
